import { distance } from '@turf/turf';
import { Position } from 'geojson';
import type { FlightPlanRouteInfo } from './flight-plan-routes-helper';

// Tbd: improve: find the closest and check which of its neighbors is the current segment
export function findNextPoint(info: FlightPlanRouteInfo, currentPosition: Position | null): number | null {
    if (!info?.points.length || !currentPosition) {
        return null;
    }

    let closestWaypointIndex = 0;
    let minDistance = Infinity;
    const { points: route } = info;

    for (let i = 1; i < route.length; i++) {
        const position = route[i].position;
        const distanceToWaypoint = distance(currentPosition, position, { units: 'nauticalmiles' });
        if (distanceToWaypoint < minDistance) {
            minDistance = distanceToWaypoint;
            closestWaypointIndex = i;
        }
    }

    if (minDistance <= 0.5) {
        return closestWaypointIndex;
    }

    // If not close to any waypoint, find which segment the aircraft is progressing towards
    // by checking which segment has the aircraft closest to its line
    let bestSegmentIndex = 0;
    let minSegmentDistance = Infinity;

    for (let i = 1; i < route.length; i++) {
        // Calculate perpendicular distance to the segment line
        const perpDistance = getPerpendicularDistance(currentPosition as [number, number], route[i - 1].position, route[i].position);

        // Also factor in how far along the segment the aircraft is
        const progressFactor = getProgressAlongSegment(currentPosition as [number, number], route[i - 1].position, route[i].position);

        // Prefer segments where aircraft is making forward progress (0 to 1)
        // and is close to the segment line
        const score = perpDistance + (progressFactor < 0 ? 100 : 0) + (progressFactor > 1 ? 50 : 0);

        if (score < minSegmentDistance) {
            minSegmentDistance = score;
            bestSegmentIndex = i;
        }
    }

    return bestSegmentIndex;
}

function getPerpendicularDistance(point: [number, number], lineStart: [number, number], lineEnd: [number, number]): number {
    const A = point[0] - lineStart[0];
    const B = point[1] - lineStart[1];
    const C = lineEnd[0] - lineStart[0];
    const D = lineEnd[1] - lineStart[1];

    const dot = A * C + B * D;
    const lenSq = C * C + D * D;

    if (lenSq === 0) {
        return distance(point, lineStart, { units: 'nauticalmiles' });
    }

    const param = Math.max(0, Math.min(1, dot / lenSq));
    const xx = lineStart[0] + param * C;
    const yy = lineStart[1] + param * D;

    return distance(point, [xx, yy], { units: 'nauticalmiles' });
}

function getProgressAlongSegment(point: [number, number], lineStart: [number, number], lineEnd: [number, number]): number {
    const A = point[0] - lineStart[0];
    const B = point[1] - lineStart[1];
    const C = lineEnd[0] - lineStart[0];
    const D = lineEnd[1] - lineStart[1];

    const dot = A * C + B * D;
    const lenSq = C * C + D * D;

    if (lenSq === 0) {
        return 0;
    }

    return dot / lenSq; // 0 = at start, 1 = at end, <0 = before start, >1 = past end
}
